import type {
  ReturnSuccess,
  ReturnError,
  Tenant,
  MandatorKey,
  BusinessPartner,
} from "./types";
import {
  CountryCode,
  mandatorsConfig,
  countryNamesByCode,
  tenantsConfig,
  mandatorByCountryCode,
} from "./config";

// ============ Return helper functions ============
const composeSuccess = <T>(data: T): ReturnSuccess<T> => ({
  data,
  error: null,
});
const composeError = (error: unknown): ReturnError => {
  const typedSafeError =
    error instanceof Error ? error : new Error(String(error));
  return { data: null, error: typedSafeError };
};

// ============ Parsers / Validation ============
const parseMandatorKey = (mandatorKey: number | string) => {
  const mandatorInt = Number(mandatorKey);
  if (Number.isNaN(mandatorInt)) {
    throw new Error(`mandatorKey '${mandatorKey}' is not a number`);
  }
  if (mandatorInt < 100 || mandatorInt > 9999) {
    throw new Error(
      `mandatorKey '${mandatorKey}' must be between 100 and 9999`,
    );
  }
  if (!(mandatorInt in mandatorsConfig)) {
    throw new Error(
      `Mandator '${mandatorInt}' is either invalid or has not yet been configured`,
    );
  }
  return mandatorInt as MandatorKey;
};

export const parseCountryCode = (code: string) => {
  const codeUpper = code.toUpperCase();
  if (!(Object.values(CountryCode) as string[]).includes(codeUpper)) {
    throw new Error(
      `Country code '${codeUpper}' is either invalid or has not yet been configured'`,
    );
  }
  return codeUpper as CountryCode;
};

const parseTenant = (tenant: string) => {
  const tenantUpper = tenant.toUpperCase();
  if (!(Object.keys(tenantsConfig) as string[]).includes(tenantUpper)) {
    throw new Error(
      `Tenant '${tenantUpper}' is either invalid or has not yet been configured'`,
    );
  }
  return tenantUpper as Tenant;
};

// ============ Getters ============
const getMandator = (mandatorKey: MandatorKey) => {
  if (!(mandatorKey in mandatorsConfig)) {
    throw new Error(
      `Mandator '${mandatorKey} is either invalid or has not yet been configured`,
    );
  }
  return mandatorsConfig[mandatorKey];
};

const getCountryCodesByMandatorKey = (mandatorKey: MandatorKey) => {
  const mandator = getMandator(mandatorKey);
  return mandator.countries;
};

const getCountryNamesByMandatorKey = (mandatorKey: MandatorKey) => {
  const mandator = getMandator(mandatorKey);
  const countryNames = mandator.countries.map((c) => countryNamesByCode[c]);
  return countryNames;
};

const getBusinessPartnersByTenant = (tenant: Tenant) => {
  if (!(tenant in tenantsConfig)) {
    return [];
  }
  return tenantsConfig[tenant]!;
};

const getMandatorByCountryCode = (countryCode: CountryCode) => {
  if (!(countryCode in mandatorByCountryCode)) {
    throw new Error(`No mandator for country code '${countryCode}' found`);
  }
  const parsedMandator = parseMandatorKey(mandatorByCountryCode[countryCode]);
  return parsedMandator;
};

const getCountryNamesByCountryCodes = (countryCodes: CountryCode[]) =>
  countryCodes.map((countryCode) => countryNamesByCode[countryCode]);

// ============ masks/filters ============
const maskCountryCodesByBusinessPartners = (
  countryCodes: CountryCode[],
  businessPartners: BusinessPartner[],
) => countryCodes.filter((code) => businessPartners.includes(code));

// ============ utils ============
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const safeRun = <T>(fn: () => any) => {
  try {
    const result = fn();
    return composeSuccess<T>(result);
  } catch (error) {
    return composeError(error);
  }
};

const filterProjectsByProductMandator = (
  projects: Array<{
    id: string;
    country?: string;
    title: string;
    mandator?: number;
  }>,
  productCountryOfOrigin: string,
  currentUserTenant: string,
) => {
  const tenant = parseTenant(currentUserTenant);
  const businessPartners = getBusinessPartnersByTenant(tenant);

  const validPartners = businessPartners.filter((partner) => {
    try {
      getMandatorByCountryCode(partner);
      return true;
    } catch {
      return false;
    }
  });

  const validMandators = new Set<number>();

  validPartners.forEach((partner) => {
    const mandatorKey = getMandatorByCountryCode(partner);
    const parsedMandator = parseMandatorKey(mandatorKey);

    const countryCodes = getCountryCodesByMandatorKey(parsedMandator);
    const countryNames = getCountryNamesByCountryCodes(countryCodes);

    if (countryNames.includes(productCountryOfOrigin)) {
      validMandators.add(parsedMandator);
    }
  });

  return projects.filter((project) => {
    if (!project.mandator) {
      return false;
    }

    const projectMandator = +project.mandator;

    const isValid = validMandators.has(projectMandator);

    return isValid;
  });
};

const getPartnersByMandator = (mandatorKey: MandatorKey, tenant: Tenant) =>
  getBusinessPartnersByTenant(tenant).filter((partner) => {
    try {
      return getMandatorByCountryCode(partner) === mandatorKey;
    } catch {
      return false;
    }
  });

// ============ Exports ============
const mandatorUtils = {
  getCountryNamesByCountryCodes,
  getCountryCodesByMandatorKey,
  getCountryNamesByMandatorKey,
  getBusinessPartnersByTenant,
  getMandatorByCountryCode,
  maskCountryCodesByBusinessPartners,
  parseCountryCode,
  parseTenant,
  parseMandatorKey,
  safeRun,
  filterProjectsByProductMandator,
  getPartnersByMandator,
};

export default mandatorUtils;
